import React, { useState, useMemo, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, User, Building2, TrendingUp, Package, DollarSign, Syringe, FlaskConical, Pill, ClipboardList, Phone, Bot, Globe, Users, Search, Bell, ChevronRight, Home, X, Mail, Palette, Sun, Moon, Stethoscope, Scissors, Boxes, Calendar, FileText, Tag, Shield } from 'lucide-react';
import ProfileSettings from '../components/settings/ProfileSettings';
import PracticeSettings from '../components/settings/PracticeSettings';
import WorkspaceAdmin from '../components/settings/WorkspaceAdmin';
import { isSuperAdmin } from '../utils/superAdmin';
import CatalogManagement from './CatalogManagement';
import CatalogItemEditPage from './CatalogItemEditPage';
import ImmunizationCatalogManagement from './ImmunizationCatalogManagement';
import LabsCatalogManagement from './LabsCatalogManagement';
import MedicationsCatalogManagement from './MedicationsCatalogManagement';
import ProceduresCatalogManagement from './ProceduresCatalogManagement';
import TreatmentsCatalogManagement from './TreatmentsCatalogManagement';
import ProductsCatalogManagement from './ProductsCatalogManagement';
import { ProductCatalogShow } from './products/ProductCatalogShow';
import { MedicationCatalogShow } from './medications/MedicationCatalogShow';
import { LabsCatalogShow } from './labs/LabsCatalogShow';
import { ProcedureCatalogShow } from './procedures/ProcedureCatalogShow';
import { TreatmentCatalogShow } from './treatments/TreatmentCatalogShow';
import { ImmunizationCatalogShow } from './immunizations/ImmunizationCatalogShow';
import Inventory from './Inventory';
import { InventoryStatusItemShow } from './inventory/InventoryStatusItemShow';
import FormsSettings from './FormsSettings';
import PhoneConfigSection from '../components/voice-agents/PhoneConfigSection';
import VoiceAgentSection from '../components/voice-agents/VoiceAgentSection';
import BookingWidget from './BookingWidget';
import PracticeTeam from './PracticeTeam';
import AppearanceSettings from '../components/settings/AppearanceSettings';
import NotificationsSettings from '../components/settings/NotificationsSettings';
import ClientTagsSettings from '../components/settings/ClientTagsSettings';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME } from '../config/branding';

// LocalStorage key for most visited settings
const SETTINGS_VISITS_KEY = 'helppet_settings_visits';

// Get visit counts from localStorage
const getVisitCounts = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(SETTINGS_VISITS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Increment visit count for a setting
const incrementVisitCount = (key: string) => {
  try {
    const counts = getVisitCounts();
    counts[key] = (counts[key] || 0) + 1;
    localStorage.setItem(SETTINGS_VISITS_KEY, JSON.stringify(counts));
  } catch {
    // Ignore storage errors
  }
};

// Splash page component
interface SettingsSplashProps {
  visibleSections: typeof settingsSectionsConfig;
  onSettingClick: (key: string) => void;
}

// Isometric 3D Icon Component
const IsometricIcon: React.FC<{ type: string }> = ({ type }) => {
  // Common isometric transform style
  const isometricStyle = {
    transform: 'rotateX(55deg) rotateZ(-45deg)',
    transformStyle: 'preserve-3d' as const,
  };

  const renderIcon = () => {
    switch (type) {
      case 'team':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            {/* Shadow */}
            <div 
              className="absolute bottom-0 w-20 h-20 bg-blue-200/50 rounded-2xl blur-xl"
              style={{ transform: 'translateY(10px)' }}
            />
            {/* Main platform */}
            <div 
              className="relative w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl shadow-xl"
              style={isometricStyle}
            >
              {/* Top surface highlight */}
              <div className="absolute inset-1 bg-gradient-to-br from-blue-400 to-transparent rounded-xl opacity-60" />
              {/* Inner card 1 */}
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg shadow-lg" />
              {/* Inner card 2 */}
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-orange-500 rounded-md shadow-lg" />
            </div>
            {/* Floating user icon - left */}
            <div 
              className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-green-400"
              style={{ transform: 'translateZ(20px)' }}
            >
              <User className="w-5 h-5 text-green-600" />
            </div>
            {/* Floating users icon - right */}
            <div 
              className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center"
              style={{ transform: 'translateZ(15px)' }}
            >
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            {/* Floating plus icon - top */}
            <div 
              className="absolute right-2 -top-1 w-7 h-7 bg-white rounded-lg shadow-lg flex items-center justify-center"
              style={{ transform: 'translateZ(25px)' }}
            >
              <span className="text-blue-600 font-bold text-sm">+</span>
            </div>
          </div>
        );
      
      case 'agents':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-green-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-green-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-orange-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-green-400">
              <Bot className="w-5 h-5 text-green-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <Phone className="w-4 h-4 text-blue-600" />
            </div>
          </div>
        );
      
      case 'forms':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-orange-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-orange-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-sky-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-orange-400">
              <ClipboardList className="w-5 h-5 text-orange-500" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <Search className="w-4 h-4 text-gray-500" />
            </div>
          </div>
        );

      case 'phones':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-teal-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-teal-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-lime-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-emerald-500">
              <Phone className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-teal-500 font-bold text-xs">#</span>
            </div>
          </div>
        );

      case 'booking-widget':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-blue-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-blue-300 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-orange-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-blue-400">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <Package className="w-4 h-4 text-orange-500" />
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-blue-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-blue-300 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-green-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-blue-400">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-blue-600 text-xs">✏️</span>
            </div>
          </div>
        );

      case 'practice':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-amber-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-amber-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-teal-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-amber-400">
              <Building2 className="w-5 h-5 text-amber-500" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-amber-600 text-xs">🏥</span>
            </div>
          </div>
        );

      case 'catalog':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-lime-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-lime-500 to-lime-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-lime-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-emerald-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-lime-500">
              <DollarSign className="w-5 h-5 text-lime-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-lime-600 text-xs">💰</span>
            </div>
          </div>
        );

      case 'immunization-catalog':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-green-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-green-300 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-blue-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-green-400">
              <Syringe className="w-5 h-5 text-green-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-green-600 text-xs">💉</span>
            </div>
          </div>
        );

      case 'labs-catalog':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-teal-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-teal-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-cyan-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-teal-400">
              <FlaskConical className="w-5 h-5 text-teal-500" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-teal-500 text-xs">🧪</span>
            </div>
          </div>
        );

      case 'medications-catalog':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-orange-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-orange-300 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-blue-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-orange-400">
              <Pill className="w-5 h-5 text-orange-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <span className="text-orange-600 text-xs">💊</span>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-indigo-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-indigo-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-amber-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-indigo-400">
              <Sun className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="absolute -right-1 top-8 w-8 h-8 bg-white rounded-lg shadow-lg flex items-center justify-center">
              <Moon className="w-4 h-4 text-indigo-500" />
            </div>
          </div>
        );

      default:
        return (
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute bottom-0 w-20 h-20 bg-gray-200/50 rounded-2xl blur-xl" style={{ transform: 'translateY(10px)' }} />
            <div className="relative w-20 h-20 bg-gradient-to-br from-gray-500 to-gray-700 rounded-2xl shadow-xl" style={isometricStyle}>
              <div className="absolute inset-1 bg-gradient-to-br from-gray-400 to-transparent rounded-xl opacity-60" />
              <div className="absolute top-3 left-3 w-6 h-6 bg-slate-800 rounded-lg" />
              <div className="absolute bottom-3 right-3 w-8 h-4 bg-blue-400 rounded-md" />
            </div>
            <div className="absolute -left-2 top-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-gray-400">
              <Package className="w-5 h-5 text-gray-500" />
            </div>
          </div>
        );
    }
  };

  return renderIcon();
};

// Small Isometric Icon for list items
const SmallIsometricIcon: React.FC<{ type: string }> = ({ type }) => {
  const isometricStyle = {
    transform: 'rotateX(55deg) rotateZ(-45deg)',
    transformStyle: 'preserve-3d' as const,
  };

  const getColors = (key: string) => {
    const colorMap: Record<string, { bg: string; accent: string; icon: string }> = {
      'profile': { bg: 'from-blue-300 to-blue-500', accent: 'bg-green-400', icon: 'text-blue-600 border-blue-300' },
      'appearance': { bg: 'from-indigo-400 to-indigo-600', accent: 'bg-amber-400', icon: 'text-indigo-600 border-indigo-300' },
      'practice': { bg: 'from-orange-400 to-orange-600', accent: 'bg-blue-400', icon: 'text-orange-600 border-orange-300' },
      'team': { bg: 'from-blue-400 to-blue-600', accent: 'bg-orange-400', icon: 'text-blue-600 border-blue-300' },
      'phones': { bg: 'from-green-400 to-green-600', accent: 'bg-orange-400', icon: 'text-green-600 border-green-300' },
      'agents': { bg: 'from-green-400 to-green-600', accent: 'bg-orange-400', icon: 'text-green-600 border-green-300' },
      'booking-widget': { bg: 'from-blue-400 to-blue-600', accent: 'bg-orange-400', icon: 'text-blue-600 border-blue-300' },
      'forms': { bg: 'from-orange-400 to-orange-600', accent: 'bg-blue-400', icon: 'text-orange-600 border-orange-300' },
      'catalog': { bg: 'from-green-400 to-green-600', accent: 'bg-blue-400', icon: 'text-green-600 border-green-300' },
      'immunization-catalog': { bg: 'from-green-400 to-green-600', accent: 'bg-blue-400', icon: 'text-green-600 border-green-300' },
      'labs-catalog': { bg: 'from-blue-400 to-blue-600', accent: 'bg-green-400', icon: 'text-blue-600 border-blue-300' },
      'medications-catalog': { bg: 'from-orange-400 to-orange-600', accent: 'bg-blue-400', icon: 'text-orange-600 border-orange-300' },
    };
    return colorMap[key] || { bg: 'from-gray-400 to-gray-600', accent: 'bg-blue-400', icon: 'text-gray-500 border-gray-300' };
  };

  const getIcon = (key: string) => {
    const icons: Record<string, React.ReactNode> = {
      'profile': <User className="w-3 h-3" />,
      'appearance': <Palette className="w-3 h-3" />,
      'practice': <Building2 className="w-3 h-3" />,
      'team': <Users className="w-3 h-3" />,
      'phones': <Phone className="w-3 h-3" />,
      'agents': <Bot className="w-3 h-3" />,
      'booking-widget': <Globe className="w-3 h-3" />,
      'forms': <ClipboardList className="w-3 h-3" />,
      'catalog': <DollarSign className="w-3 h-3" />,
      'immunization-catalog': <Syringe className="w-3 h-3" />,
      'labs-catalog': <FlaskConical className="w-3 h-3" />,
      'medications-catalog': <Pill className="w-3 h-3" />,
    };
    return icons[key] || <Package className="w-3 h-3" />;
  };

  const colors = getColors(type);
  const icon = getIcon(type);

  return (
    <div className="relative w-10 h-10 flex items-center justify-center">
      {/* Shadow */}
      <div className="absolute bottom-0 w-6 h-6 bg-gray-200/50 rounded-lg blur-sm" style={{ transform: 'translateY(2px)' }} />
      {/* Platform */}
      <div 
        className={`relative w-6 h-6 bg-gradient-to-br ${colors.bg} rounded-lg shadow-md`}
        style={isometricStyle}
      >
        <div className="absolute inset-0.5 bg-gradient-to-br from-white/30 to-transparent rounded-md" />
        <div className="absolute top-1 left-1 w-1.5 h-1.5 bg-slate-700 rounded-sm" />
        <div className={`absolute bottom-1 right-1 w-2 h-1 ${colors.accent} rounded-sm`} />
      </div>
      {/* Floating icon */}
      <div className={`absolute -left-0.5 top-1 w-5 h-5 bg-white rounded-full shadow-md flex items-center justify-center border ${colors.icon}`}>
        {icon}
      </div>
    </div>
  );
};

const settingsSectionsConfig = [
  { key: 'profile', label: 'Profile', description: 'Update your personal info', icon: <User className="w-5 h-5" />, path: '/settings/profile', section: 'ACCOUNT', color: 'bg-blue-100 text-blue-600' },
  { key: 'appearance', label: 'Appearance', description: 'Customize theme and display', icon: <Palette className="w-5 h-5" />, path: '/settings/appearance', section: 'ACCOUNT', color: 'bg-indigo-100 text-indigo-600' },
  { key: 'practice', label: 'Practice', description: 'Manage practice details', icon: <Building2 className="w-5 h-5" />, path: '/settings/practice', section: 'ACCOUNT', color: 'bg-orange-100 text-orange-600' },
  { key: 'notifications', label: 'In-App Notifications', description: 'Control in-app notifications', icon: <Bell className="w-5 h-5" />, path: '/settings/notifications', section: 'NOTIFICATIONS', color: 'bg-amber-100 text-amber-600' },
  { key: 'team', label: 'Team', description: 'Invite and manage team members', icon: <Users className="w-5 h-5" />, path: '/settings/team', section: 'ACCOUNT', adminOnly: true, color: 'bg-blue-100 text-blue-600' },
  { key: 'client-tags', label: 'Client Tags', description: 'Configure client tag suggestions', icon: <Tag className="w-5 h-5" />, path: '/settings/client-tags', section: 'CRM', adminOnly: true, color: 'bg-teal-100 text-teal-600' },
  { key: 'phones', label: 'Phone Numbers', description: 'Configure phone numbers', icon: <Phone className="w-5 h-5" />, path: '/settings/phones', section: 'FRONT DESK', adminOnly: true, color: 'bg-green-100 text-green-600' },
  { key: 'agents', label: 'Voice Agents', description: 'Set up AI voice agents', icon: <Bot className="w-5 h-5" />, path: '/settings/agents', section: 'FRONT DESK', adminOnly: true, color: 'bg-green-100 text-green-600' },
  { key: 'booking-widget', label: 'Online Booking', description: 'Configure online booking widget', icon: <Globe className="w-5 h-5" />, path: '/settings/booking-widget', section: 'FRONT DESK', adminOnly: true, color: 'bg-blue-100 text-blue-600' },
  { key: 'forms', label: 'Forms', description: 'Create and manage forms', icon: <ClipboardList className="w-5 h-5" />, path: '/settings/forms', section: 'PATIENT FORMS', adminOnly: true, color: 'bg-orange-100 text-orange-600' },
  { key: 'catalog', label: 'Billing Catalog', description: 'Manage billing items', icon: <DollarSign className="w-5 h-5" />, path: '/settings/catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-green-100 text-green-600' },
  { key: 'inventory', label: 'Inventory Management', description: 'Stock levels and purchase orders', icon: <Boxes className="w-5 h-5" />, path: '/settings/inventory', section: 'CATALOGS', adminOnly: true, color: 'bg-emerald-100 text-emerald-600' },
  { key: 'immunization-catalog', label: 'Immunization Catalog', description: 'Manage immunizations', icon: <Syringe className="w-5 h-5" />, path: '/settings/immunization-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-green-100 text-green-600' },
  { key: 'labs-catalog', label: 'Labs', description: 'CBC, Chemistry, Fecals, Urinalysis', icon: <FlaskConical className="w-5 h-5" />, path: '/settings/labs-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-blue-100 text-blue-600' },
  { key: 'diagnostics-catalog', label: 'Diagnostics', description: 'Physical Exams, Imaging, Vitals', icon: <Stethoscope className="w-5 h-5" />, path: '/settings/diagnostics-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-indigo-100 text-indigo-600' },
  { key: 'medications-catalog', label: 'Medications Catalog', description: 'Manage medications', icon: <Pill className="w-5 h-5" />, path: '/settings/medications-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-orange-100 text-orange-600' },
  { key: 'treatments-catalog', label: 'Treatments', description: 'Wound Care, Dental, Grooming', icon: <Scissors className="w-5 h-5" />, path: '/settings/treatments-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-amber-100 text-amber-600' },
  { key: 'products-catalog', label: 'Products', description: 'E-Collars, Supplies, Food', icon: <Package className="w-5 h-5" />, path: '/settings/products-catalog', section: 'CATALOGS', adminOnly: true, color: 'bg-teal-100 text-teal-600' },
];

const SettingsSplash: React.FC<SettingsSplashProps> = ({ visibleSections, onSettingClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSupportModal) {
        setShowSupportModal(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showSupportModal]);

  // Reset selected index when search query or results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Get most visited settings (top 3)
  const mostVisited = useMemo(() => {
    const counts = getVisitCounts();
    const defaultSettings = ['team', 'agents', 'forms'];
    
    // Sort by visit count
    const sorted = visibleSections
      .filter(s => counts[s.key] > 0)
      .sort((a, b) => (counts[b.key] || 0) - (counts[a.key] || 0))
      .slice(0, 3);
    
    // If less than 3, fill with defaults
    if (sorted.length < 3) {
      const remaining = visibleSections
        .filter(s => !sorted.find(x => x.key === s.key) && defaultSettings.includes(s.key))
        .slice(0, 3 - sorted.length);
      return [...sorted, ...remaining];
    }
    
    return sorted;
  }, [visibleSections]);

  // Filter settings by search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return visibleSections.filter(
      s => s.label.toLowerCase().includes(query) || 
           s.description?.toLowerCase().includes(query) ||
           s.section.toLowerCase().includes(query)
    );
  }, [visibleSections, searchQuery]);

  const handleSettingClick = (section: typeof visibleSections[0]) => {
    onSettingClick(section.key);
    navigate(section.path);
  };

  // Handle keyboard navigation for search results
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchQuery.trim() || filteredSections.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSections.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredSections[selectedIndex]) {
          handleSettingClick(filteredSections[selectedIndex]);
        }
        break;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Search Section */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Find the setting you need</h2>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search settings..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-xl text-gray-900 dark:text-dark-text-primary placeholder-gray-500 dark:placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Search Results */}
        {searchQuery.trim() && (
          <div className="mt-4 space-y-1">
            {filteredSections.length > 0 ? (
              <>
                <div className="text-xs text-gray-400 dark:text-dark-text-muted mb-2 px-1">
                  Use ↑↓ to navigate, Enter to select
                </div>
                {filteredSections.map((section, index) => (
                  <button
                    key={section.key}
                    onClick={() => handleSettingClick(section)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center gap-4 p-3 rounded-xl transition-colors text-left ${
                      index === selectedIndex
                        ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500'
                        : 'hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                    }`}
                  >
                    <SmallIsometricIcon type={section.key} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-dark-text-primary">{section.label}</div>
                      <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{section.description}</div>
                    </div>
                    {index === selectedIndex && (
                      <ChevronRight className="w-5 h-5 text-blue-500" />
                    )}
                  </button>
                ))}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-dark-text-muted">No settings found for "{searchQuery}"</div>
            )}
          </div>
        )}
      </div>

      {/* Most Visited Section */}
      {!searchQuery.trim() && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-6">Most visited settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mostVisited.map((section) => (
              <button
                key={section.key}
                onClick={() => handleSettingClick(section)}
                className="group flex flex-col items-center p-6 rounded-2xl bg-gray-50 dark:bg-dark-bg-tertiary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-all text-center"
              >
                <div className="mb-2 group-hover:scale-105 transition-transform">
                  <IsometricIcon type={section.key} />
                </div>
                <div className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">{section.label}</div>
                <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{section.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Looking for something else? */}
      {!searchQuery.trim() && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Looking for something else?</h2>
          <button
            onClick={() => setShowSupportModal(true)}
            className="w-full flex items-center gap-4 p-4 bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group text-left"
          >
            <div className="w-12 h-12 bg-gray-100 dark:bg-dark-bg-tertiary rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-gray-500 dark:text-dark-text-secondary" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 dark:text-dark-text-primary">Contact Support</div>
              <div className="text-sm text-gray-500 dark:text-dark-text-secondary">Get help from our team with any questions or issues.</div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-dark-text-muted group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}

      {/* All Settings by Category */}
      {!searchQuery.trim() && (
        <div className="mt-8 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">All settings</h2>
          
          {['ACCOUNT', 'NOTIFICATIONS', 'FRONT DESK', 'PATIENT FORMS', 'CATALOGS', 'CRM', 'SUPER ADMIN'].map((sectionName) => {
            const sectionItems = visibleSections.filter(s => s.section === sectionName);
            if (sectionItems.length === 0) return null;
            
            return (
              <div key={sectionName} className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
                <div className="px-6 py-3 bg-gray-50 dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">{sectionName}</h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-dark-border">
                  {sectionItems.map((section) => (
                    <button
                      key={section.key}
                      onClick={() => handleSettingClick(section)}
                      className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors text-left"
                    >
                      <SmallIsometricIcon type={section.key} />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-dark-text-primary">{section.label}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{section.description}</div>
                      </div>
                      <ArrowLeft className="w-5 h-5 text-gray-400 dark:text-dark-text-muted rotate-180" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20 dark:bg-black/50" onClick={() => setShowSupportModal(false)} />
          <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl max-w-md w-full relative z-10 overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Isometric Help Graphic */}
            <div className="relative bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-blue-900/20 dark:via-dark-bg-secondary dark:to-orange-900/20 py-8 px-6">
              <button
                onClick={() => setShowSupportModal(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text-primary transition-colors shadow-md border border-gray-100 dark:border-dark-border z-10"
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* Isometric Scene */}
              <div className="flex justify-center items-center h-48">
                <div className="relative" style={{ perspective: '1000px' }}>
                  {/* Main Platform */}
                  <div 
                    className="relative w-40 h-40"
                    style={{ 
                      transform: 'rotateX(55deg) rotateZ(-45deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    {/* Base shadow */}
                    <div className="absolute -bottom-4 left-4 w-full h-full bg-blue-200/30 dark:bg-blue-800/30 rounded-3xl blur-xl" />
                    
                    {/* Main platform */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl shadow-2xl">
                      <div className="absolute inset-1 bg-gradient-to-br from-blue-400 to-transparent rounded-2xl opacity-50" />
                      
                      {/* Screen/Display area */}
                      <div className="absolute top-4 left-4 right-4 bottom-12 bg-slate-800 rounded-xl overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
                        {/* Screen content lines */}
                        <div className="absolute top-3 left-3 right-6 h-2 bg-green-400/60 rounded" />
                        <div className="absolute top-7 left-3 right-8 h-1.5 bg-blue-300/40 rounded" />
                        <div className="absolute top-10 left-3 right-10 h-1.5 bg-blue-300/40 rounded" />
                      </div>
                      
                      {/* Bottom accent bar */}
                      <div className="absolute bottom-4 left-4 right-4 h-5 bg-orange-500 rounded-lg shadow-lg" />
                    </div>
                  </div>
                  
                  {/* Floating Bell Icon - moved to left, smaller */}
                  <div className="absolute top-2 -left-6 w-12 h-12 bg-white dark:bg-dark-bg-tertiary rounded-xl shadow-xl flex items-center justify-center border-2 border-orange-400">
                    <Bell className="w-6 h-6 text-orange-500" />
                    {/* Notification dot */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full shadow-lg animate-pulse" />
                  </div>
                  
                  {/* Floating mail icon - left bottom */}
                  <div className="absolute top-20 -left-4 w-10 h-10 bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-lg flex items-center justify-center border border-blue-200 dark:border-blue-800">
                    <Mail className="w-5 h-5 text-blue-500" />
                  </div>
                  
                  {/* Floating phone icon - right */}
                  <div className="absolute top-12 -right-4 w-10 h-10 bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-lg flex items-center justify-center border border-green-200 dark:border-green-800">
                    <Phone className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Need Help?</h3>
              </div>

              <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-6">
                Our support team is here to help you with any questions or issues you may have.
              </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
                <Phone className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-1">Phone</div>
                  <a
                    href="tel:+14157549214"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    (415) 754-9214
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
                <Mail className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-1">Email</div>
                  <a
                    href="mailto:hi@helppet.ai"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    hi@helppet.ai
                  </a>
                </div>
              </div>
            </div>

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const showSuperAdmin = isSuperAdmin(user);
  
  const settingsSections = [
    { key: 'profile', label: 'Profile', icon: <User className="w-4 h-4" />, path: '/settings/profile', section: 'ACCOUNT' },
    { key: 'appearance', label: 'Appearance', icon: <Palette className="w-4 h-4" />, path: '/settings/appearance', section: 'ACCOUNT' },
    { key: 'practice', label: 'Practice', icon: <Building2 className="w-4 h-4" />, path: '/settings/practice', section: 'ACCOUNT' },
    { key: 'team', label: 'Team', icon: <Users className="w-4 h-4" />, path: '/settings/team', section: 'ACCOUNT', adminOnly: true },
    { key: 'notifications', label: 'In-App Notifications', icon: <Bell className="w-4 h-4" />, path: '/settings/notifications', section: 'NOTIFICATIONS' },
    { key: 'client-tags', label: 'Client Tags', icon: <Tag className="w-4 h-4" />, path: '/settings/client-tags', section: 'CRM', adminOnly: true },
    { key: 'phones', label: 'Phone Numbers', icon: <Phone className="w-4 h-4" />, path: '/settings/phones', section: 'FRONT DESK', adminOnly: true },
    { key: 'agents', label: 'Voice Agents', icon: <Bot className="w-4 h-4" />, path: '/settings/agents', section: 'FRONT DESK', adminOnly: true },
    { key: 'booking-widget', label: 'Online Booking', icon: <Globe className="w-4 h-4" />, path: '/settings/booking-widget', section: 'FRONT DESK', adminOnly: true },
    { key: 'forms', label: 'Forms', icon: <ClipboardList className="w-4 h-4" />, path: '/settings/forms', section: 'PATIENT FORMS', adminOnly: true },
    { key: 'catalog', label: 'Billing Catalog', icon: <DollarSign className="w-4 h-4" />, path: '/settings/catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'inventory', label: 'Inventory Management', icon: <Boxes className="w-4 h-4" />, path: '/settings/inventory', section: 'CATALOGS', adminOnly: true },
    { key: 'immunization-catalog', label: 'Immunization Catalog', icon: <Syringe className="w-4 h-4" />, path: '/settings/immunization-catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'labs-catalog', label: 'Labs', icon: <FlaskConical className="w-4 h-4" />, path: '/settings/labs-catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'diagnostics-catalog', label: 'Diagnostics', icon: <Stethoscope className="w-4 h-4" />, path: '/settings/diagnostics-catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'medications-catalog', label: 'Medications Catalog', icon: <Pill className="w-4 h-4" />, path: '/settings/medications-catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'treatments-catalog', label: 'Treatments', icon: <Scissors className="w-4 h-4" />, path: '/settings/treatments-catalog', section: 'CATALOGS', adminOnly: true },
    { key: 'products-catalog', label: 'Products', icon: <Package className="w-4 h-4" />, path: '/settings/products-catalog', section: 'CATALOGS', adminOnly: true },
    ...(showSuperAdmin ? [{ key: 'workspace-admin', label: 'Workspace Admin', icon: <Shield className="w-4 h-4" />, path: '/settings/workspace-admin', section: 'SUPER ADMIN' }] : []),
  ];

  // Filter sections based on user role
  const isAdminOrPracticeAdmin = user?.role === 'ADMIN' || user?.role === 'PRACTICE_ADMIN';
  const visibleSections = settingsSections.filter(section =>
    !section.adminOnly || (section.adminOnly && isAdminOrPracticeAdmin)
  );

  // Also filter the config for splash page
  const visibleSectionsConfig = [
    ...settingsSectionsConfig.filter(section =>
      !section.adminOnly || (section.adminOnly && isAdminOrPracticeAdmin)
    ),
    ...(showSuperAdmin ? [{ key: 'workspace-admin', label: 'Workspace Admin', description: 'Manage organization seats and access', icon: <Shield className="w-5 h-5" />, path: '/settings/workspace-admin', section: 'SUPER ADMIN', color: 'bg-blue-100 text-blue-600' }] : []),
  ];

  const handleBack = () => {
    navigate('/dashboard/appointments');
  };

  const handleSettingClick = (key: string) => {
    incrementVisitCount(key);
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-bg-primary">
      {/* Settings Sidebar */}
      <div className="w-64 bg-white dark:bg-dark-bg-secondary border-r border-gray-200 dark:border-dark-border flex flex-col">
        {/* Logo/Brand Section */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border">
              <img
                src="/logo_clear_back.png"
                alt={APP_NAME}
                className="w-full h-full object-contain p-1"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{APP_NAME}</div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted">{user?.full_name || 'Settings'}</div>
            </div>
          </div>
          <button
            onClick={handleBack}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to app</span>
          </button>
        </div>

        {/* Settings Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {/* Settings Home Link */}
          <div className="mb-4">
            <Link
              to="/settings"
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${location.pathname === '/settings'
                  ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                  : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                }`}
            >
              <Home className="w-4 h-4" />
              <span>Settings Home</span>
            </Link>
          </div>

          {/* Account Section */}
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Account</div>
            <div className="space-y-1">
              {visibleSections.filter(s => s.section === 'ACCOUNT').map((section) => {
                const isActive = location.pathname === section.path;
                return (
                  <Link
                    key={section.key}
                    to={section.path}
                    onClick={() => handleSettingClick(section.key)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                        ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                        : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                      }`}
                  >
                    {section.icon}
                    <span>{section.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Notifications Section */}
          {visibleSections.filter(s => s.section === 'NOTIFICATIONS').length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Notifications</div>
              <div className="space-y-1">
                {visibleSections.filter(s => s.section === 'NOTIFICATIONS').map((section) => {
                  const isActive = location.pathname === section.path;
                  return (
                    <Link
                      key={section.key}
                      to={section.path}
                      onClick={() => handleSettingClick(section.key)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                          : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Front Desk Section */}
          {visibleSections.filter(s => s.section === 'FRONT DESK').length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Front Desk</div>
              <div className="space-y-1">
                {visibleSections.filter(s => s.section === 'FRONT DESK').map((section) => {
                  const isActive = location.pathname === section.path;
                  return (
                    <Link
                      key={section.key}
                      to={section.path}
                      onClick={() => handleSettingClick(section.key)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                          : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Social Marketing Section */}
          {visibleSections.filter(s => s.section === 'PATIENT FORMS').length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Patient Forms</div>
              <div className="space-y-1">
                {visibleSections.filter(s => s.section === 'PATIENT FORMS').map((section) => {
                  const isActive = location.pathname === section.path;
                  return (
                    <Link
                      key={section.key}
                      to={section.path}
                      onClick={() => handleSettingClick(section.key)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                          : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Catalogs Section */}
          {visibleSections.filter(s => s.section === 'CATALOGS').length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Catalogs</div>
              <div className="space-y-1">
                {visibleSections.filter(s => s.section === 'CATALOGS').map((section) => {
                  const isActive = location.pathname === section.path;
                  return (
                    <Link
                      key={section.key}
                      to={section.path}
                      onClick={() => handleSettingClick(section.key)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                          : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Super Admin Section */}
          {visibleSections.filter(s => s.section === 'SUPER ADMIN').length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-3">Super Admin</div>
              <div className="space-y-1">
                {visibleSections.filter(s => s.section === 'SUPER ADMIN').map((section) => {
                  const isActive = location.pathname === section.path;
                  return (
                    <Link
                      key={section.key}
                      to={section.path}
                      onClick={() => handleSettingClick(section.key)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium'
                          : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                    >
                      {section.icon}
                      <span>{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-gray-50 dark:bg-dark-bg-primary">
        <Routes>
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="appearance" element={<AppearanceSettings />} />
          <Route path="notifications" element={<NotificationsSettings />} />
          <Route path="practice" element={<PracticeSettings />} />
          <Route path="team" element={<PracticeTeam />} />
          <Route path="client-tags" element={<ClientTagsSettings />} />
          <Route path="phones" element={<PhoneConfigSection />} />
          <Route path="agents" element={<VoiceAgentSection />} />
          <Route path="booking-widget" element={<BookingWidget />} />
          <Route path="forms" element={<FormsSettings />} />
          <Route path="catalog" element={<CatalogManagement />} />
          <Route path="catalog/:itemId/edit" element={<CatalogItemEditPage />} />
          <Route path="inventory/status/:catalogType/:catalogItemId" element={<InventoryStatusItemShow />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="immunization-catalog" element={<ImmunizationCatalogManagement />} />
          <Route path="immunization-catalog/:immunizationId" element={<ImmunizationCatalogShow />} />
          <Route path="labs-catalog" element={<LabsCatalogManagement />} />
          <Route path="labs-catalog/:diagnosticId" element={<LabsCatalogShow />} />
          <Route path="diagnostics-catalog" element={<ProceduresCatalogManagement />} />
          <Route path="diagnostics-catalog/:procedureId" element={<ProcedureCatalogShow />} />
          <Route path="medications-catalog" element={<MedicationsCatalogManagement />} />
          <Route path="medications-catalog/:medicationId" element={<MedicationCatalogShow />} />
          <Route path="treatments-catalog" element={<TreatmentsCatalogManagement />} />
          <Route path="treatments-catalog/:treatmentId" element={<TreatmentCatalogShow />} />
          <Route path="products-catalog" element={<ProductsCatalogManagement />} />
          <Route path="products-catalog/:productId" element={<ProductCatalogShow />} />
          {showSuperAdmin && <Route path="workspace-admin" element={<WorkspaceAdmin />} />}
          <Route index element={<SettingsSplash visibleSections={visibleSectionsConfig} onSettingClick={handleSettingClick} />} />
        </Routes>
      </div>
    </div>
  );
};

export default Settings;

